import { useMemo, useState } from 'react';
import {
  useAssignTenantUserGroupMutation,
  useUnassignTenantUserGroupMutation,
} from '../../features/tenant/mutations/useTenantGroupMutations';
import {
  useAssignTenantUserLabelMutation,
  useUnassignTenantUserLabelMutation,
} from '../../features/tenant/mutations/useTenantLabelMutations';
import { useTenantGroupsQuery } from '../../features/tenant/queries/useTenantGroupsQuery';
import { useTenantLabelsQuery } from '../../features/tenant/queries/useTenantLabelsQuery';
import { useTenantUserGroupsQuery } from '../../features/tenant/queries/useTenantUserGroupsQuery';
import { useTenantUserLabelsQuery } from '../../features/tenant/queries/useTenantUserLabelsQuery';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import { TenantUserAssignmentBlock } from './TenantUserAssignmentBlock';
import './TenantUserLabelsGroupsCard.css';

type TenantUserLabelsGroupsCardProps = {
  userId: string;
};

export function TenantUserLabelsGroupsCard({
  userId,
}: TenantUserLabelsGroupsCardProps) {
  const { translate } = useI18n();
  const labelsQuery = useTenantLabelsQuery();
  const groupsQuery = useTenantGroupsQuery();
  const userLabelsQuery = useTenantUserLabelsQuery(userId);
  const userGroupsQuery = useTenantUserGroupsQuery(userId);
  const assignLabelMutation = useAssignTenantUserLabelMutation();
  const unassignLabelMutation = useUnassignTenantUserLabelMutation();
  const assignGroupMutation = useAssignTenantUserGroupMutation();
  const unassignGroupMutation = useUnassignTenantUserGroupMutation();

  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const assignedLabelIds = useMemo(
    () => new Set((userLabelsQuery.data ?? []).map((label) => label.id)),
    [userLabelsQuery.data],
  );
  const assignedGroupIds = useMemo(
    () => new Set((userGroupsQuery.data ?? []).map((group) => group.id)),
    [userGroupsQuery.data],
  );

  const labelOptions = useMemo(
    () =>
      (labelsQuery.data ?? [])
        .filter((label) => !assignedLabelIds.has(label.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((label) => ({ value: label.id, label: label.name })),
    [assignedLabelIds, labelsQuery.data],
  );

  const groupOptions = useMemo(
    () =>
      (groupsQuery.data ?? [])
        .filter((group) => !assignedGroupIds.has(group.id))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => ({ value: group.id, label: group.name })),
    [assignedGroupIds, groupsQuery.data],
  );

  const isLoading =
    labelsQuery.isLoading ||
    groupsQuery.isLoading ||
    userLabelsQuery.isLoading ||
    userGroupsQuery.isLoading;

  const isError =
    labelsQuery.isError ||
    groupsQuery.isError ||
    userLabelsQuery.isError ||
    userGroupsQuery.isError;

  if (isLoading) {
    return (
      <section className="tenant-user-assignments-card">
        <p className="tenant-user-assignments-card__status">
          {translate('organisationManagement.labelsAndGroups.userCardLoading')}
        </p>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="tenant-user-assignments-card">
        <p className="tenant-user-assignments-card__status" role="alert">
          {translate(
            'organisationManagement.labelsAndGroups.userCardLoadError',
          )}
        </p>
      </section>
    );
  }

  return (
    <section className="tenant-user-assignments-card">
      <div>
        <h2 className="tenant-user-assignments-card__title">
          {translate('organisationManagement.labelsAndGroups.userCardTitle')}
        </h2>
        <p className="tenant-user-assignments-card__description">
          {translate(
            'organisationManagement.labelsAndGroups.userCardDescription',
          )}
        </p>
      </div>

      <TenantUserAssignmentBlock
        heading={translate(
          'organisationManagement.labelsAndGroups.groupsTitle',
        )}
        emptyLabel={translate(
          'organisationManagement.labelsAndGroups.userGroupsEmpty',
        )}
        chips={userGroupsQuery.data ?? []}
        chipModifierClassName="tenant-user-assignments-card__chip--group"
        removeAriaLabel={(name) =>
          translate(
            'organisationManagement.labelsAndGroups.removeGroupFromUser',
            { name },
          )
        }
        onRemove={(groupId) => {
          setPendingId(groupId);
          unassignGroupMutation.mutate(
            { userId, groupId },
            {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.unassignGroupSuccessTitle',
                  ),
                  description: '',
                });
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.unassignGroupErrorTitle',
                  ),
                );
              },
              onSettled: () => setPendingId(null),
            },
          );
        }}
        selectedId={selectedGroupId}
        onSelectedIdChange={setSelectedGroupId}
        options={groupOptions}
        selectPlaceholder={translate(
          'organisationManagement.labelsAndGroups.selectGroupPlaceholder',
        )}
        selectAriaLabel={translate(
          'organisationManagement.labelsAndGroups.selectGroupLabel',
        )}
        addLabel={translate('organisationManagement.labelsAndGroups.addGroup')}
        onAdd={() => {
          if (selectedGroupId === '') {
            return;
          }
          const groupId = selectedGroupId;
          setPendingId(groupId);
          assignGroupMutation.mutate(
            { userId, groupId },
            {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.assignGroupSuccessTitle',
                  ),
                  description: '',
                });
                setSelectedGroupId('');
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.assignGroupErrorTitle',
                  ),
                );
              },
              onSettled: () => setPendingId(null),
            },
          );
        }}
        pendingId={pendingId}
      />

      <TenantUserAssignmentBlock
        heading={translate(
          'organisationManagement.labelsAndGroups.labelsTitle',
        )}
        emptyLabel={translate(
          'organisationManagement.labelsAndGroups.userLabelsEmpty',
        )}
        chips={userLabelsQuery.data ?? []}
        removeAriaLabel={(name) =>
          translate(
            'organisationManagement.labelsAndGroups.removeLabelFromUser',
            { name },
          )
        }
        onRemove={(labelId) => {
          setPendingId(labelId);
          unassignLabelMutation.mutate(
            { userId, labelId },
            {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.unassignLabelSuccessTitle',
                  ),
                  description: '',
                });
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.unassignLabelErrorTitle',
                  ),
                );
              },
              onSettled: () => setPendingId(null),
            },
          );
        }}
        selectedId={selectedLabelId}
        onSelectedIdChange={setSelectedLabelId}
        options={labelOptions}
        selectPlaceholder={translate(
          'organisationManagement.labelsAndGroups.selectLabelPlaceholder',
        )}
        selectAriaLabel={translate(
          'organisationManagement.labelsAndGroups.selectLabelLabel',
        )}
        addLabel={translate('organisationManagement.labelsAndGroups.addLabel')}
        onAdd={() => {
          if (selectedLabelId === '') {
            return;
          }
          const labelId = selectedLabelId;
          setPendingId(labelId);
          assignLabelMutation.mutate(
            { userId, labelId },
            {
              onSuccess: () => {
                toast.success({
                  title: translate(
                    'organisationManagement.labelsAndGroups.assignLabelSuccessTitle',
                  ),
                  description: '',
                });
                setSelectedLabelId('');
              },
              onError: (error) => {
                void toastMutationError(
                  error,
                  translate(
                    'organisationManagement.labelsAndGroups.assignLabelErrorTitle',
                  ),
                );
              },
              onSettled: () => setPendingId(null),
            },
          );
        }}
        pendingId={pendingId}
      />
    </section>
  );
}
